import React from 'react';
import mapVOPStatusToDisplay from 'components/Payments/VOP/mapVOPStatusToDisplay';
import VOPStatusBadge from 'components/Payments/VOP/VOPStatusBadge';
import moment from 'moment';
import t from 'utils/translationHelper';

import { PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { Box } from '@alpha/ui-lib/ui/external';
import { Typography } from '@alpha/ui-lib/ui/Typography';

import DragAndDrop from '../../../../assets/paymentUpload.svg';
import { formatCurrency } from '../../../../utils/currency.helpers';
import { cutString } from '../ApprovalDrawer/ApprovalSummary';
import { FormikType } from '../CreatePaymentDrawer/formData';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';

type Props = {
  setFieldValue?: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  values: FormikType;
  addNewPayment?: () => void;
};

const Summary: React.FC<Props> = ({ values, setFieldValue, addNewPayment }) => {
  if (!values.payments?.length) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 'auto',
          paddingTop: '20px',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            width: '328px',
            height: '243px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '165px',
              height: '165px',
              backgroundColor: 'rgba(222, 222, 222,0.20)',
              borderRadius: '50%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img src={DragAndDrop} alt='Empty payments' />
          </div>
          <Typography
            variant='h3'
            style={{ marginTop: '24px', marginBottom: '8px' }}
          >
            {t('no_payments_added')}
          </Typography>
          <Typography
            variant='subtitle1'
            style={{ color: 'rgba(33,37,41,0.4)' }}
          >
            {t('you_can_add_a_payment_from_the_add_payments_tab')}
          </Typography>
        </div>
      </div>
    );
  }

  const rows = values.payments.map((p) => ({
    vopDetails: p.vopDetails,
    vopRequired: p.vopRequired,
    beneficiaryName: p.beneficiary!.name,
    amount: +p.amount!,
    reference: p.reference,
    purpose: p.purpose,
    date: p.date,
    currencyCode: p.beneficiary!.currencyCode,
  }));

  const vopMessage = (status: PaymentVopMatchStatus) => {
    const statusToDisplay = mapVOPStatusToDisplay(status);
    return (
      <>
        <Box marginBottom={'4px'}>
          <span style={{ color: '#F5EFF780' }}>
            {t('verification_accepted')}
          </span>
        </Box>
        <div>{statusToDisplay ?? ''}</div>
      </>
    );
  };

  return (
    <Box>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <tr>
          <td
            style={{
              marginRight: '32px',
              fontWeight: 'bold',
              color: '#999999',
              paddingLeft: '35px',
            }}
          >
            {t('beneficiary')}
          </td>
          <td
            style={{
              marginRight: '32px',
              fontWeight: 'bold',
              color: '#999999',
            }}
          >
            {t('amount')}
          </td>
          <td
            style={{
              marginRight: '32px',
              fontWeight: 'bold',
              color: '#999999',
            }}
          >
            {t('reference_(payment_summary)')}
          </td>
          <td
            style={{
              marginRight: '32px',
              fontWeight: 'bold',
              color: '#999999',
            }}
          >
            {t('purpose')}
          </td>
          <td
            style={{
              marginRight: '32px',
              fontWeight: 'bold',
              color: '#999999',
            }}
          >
            {t('date')}
          </td>
        </tr>
        <tr
          style={{
            borderBottom: '1px solid rgba(112,112,112,0.3)',
            height: '8px',
          }}
        />
        <tr style={{ height: '16px' }} />
        {rows.map((r, i) => (
          <>
            {i > 0 && (
              <>
                <tr
                  style={{
                    borderBottom: '1px solid rgba(112,112,112,0.3)',
                    height: '8px',
                  }}
                />
                <tr style={{ height: '8px' }} />
              </>
            )}
            <tr>
              <td
                style={{
                  width: '60%',
                  color: 'black',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {r.vopRequired && (
                  <VOPStatusBadge
                    status={r.vopDetails?.status}
                    message={vopMessage(
                      r.vopDetails?.status ??
                        PaymentVopMatchStatus.NOT_AVAILABLE
                    )}
                    tooltipProps={{ placement: 'left' }}
                  />
                )}
                <span
                  style={{
                    color: 'black',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    marginLeft: r.vopRequired ? '0px' : '35px',
                  }}
                >
                  {cutString(r.beneficiaryName, 20)}
                </span>
              </td>
              <td style={{ width: '15%', color: 'black', fontWeight: '500' }}>
                {cutString(formatCurrency(r.currencyCode, r.amount))}
              </td>
              <td style={{ width: '15%', color: 'black', fontWeight: '500' }}>
                {cutString(r.reference)}
              </td>
              <td style={{ width: '15%', color: 'black', fontWeight: '500' }}>
                {cutString(
                  typeof r.purpose === 'string'
                    ? r.purpose?.toString()
                    : r.purpose?.description
                )}
              </td>
              <td style={{ width: '15%', color: 'black', fontWeight: '500' }}>
                {cutString(moment(r.date!).format('DD/MM/YY').toString())}
              </td>
            </tr>
          </>
        ))}
      </table>
      <Box width="100%" display="flex" justifyContent="flex-end">
      { addNewPayment && (<ActionButton
        style={{
          backgroundColor: '#1E87771A',
          color: '#1E8777',
          borderRadius: '8px',
          padding: '12px 16px',
          marginTop: '24px',
          textAlign: 'right',
        }}
        startIcon={<FontAwesomeIcon icon={faPlusCircle as IconProp} />}
        onClick={() => {
          addNewPayment();
        }}
      >
        {t('add_new_payment')}
      </ActionButton>)}
      </Box>
    </Box>
  );
};

export default Summary;
